import jexl from 'jexl';
import {
  dateAdd,
  dateDiff,
  dayOfWeek,
  contains,
  empty,
  size,
  slug,
  num,
  str,
  round,
} from './functions';

jexl.addFunction('dateAdd', dateAdd);
jexl.addFunction('dateDiff', dateDiff);
jexl.addFunction('dayOfWeek', dayOfWeek);
jexl.addFunction('contains', contains);
jexl.addFunction('empty', empty);
jexl.addFunction('size', size);
jexl.addFunction('slug', slug);
jexl.addFunction('num', num);
jexl.addFunction('str', str);
jexl.addFunction('round', round);

export type ScopeBundle = Record<string, unknown>;

const MAX_EXPRESSION_LENGTH = 2048;

const DANGEROUS_KEYS = new Set(["__proto__", "constructor", "prototype"]);

function stripDangerousKeys(
  src: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = Object.create(null);
  for (const [k, v] of Object.entries(src)) {
    if (DANGEROUS_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

// Preserves null results (ADR §15); returns fallback only on undefined or thrown errors.
export function evaluateExpression<T>(
  expr: string,
  scope: Record<string, unknown>,
  fallback: T
): T {
  if (typeof expr !== "string") {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[formula] Rejected non-string expression (typeof ${typeof expr})`,
      );
    }
    return fallback;
  }
  if (expr.length > MAX_EXPRESSION_LENGTH) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[formula] Rejected expression: length ${expr.length} exceeds cap ${MAX_EXPRESSION_LENGTH}`,
      );
    }
    return fallback;
  }
  try {
    const result = jexl.evalSync(expr, scope);
    return (result === undefined ? fallback : result) as T;
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[formula] JEXL eval failed for "${expr}":`, err);
    }
    return fallback;
  }
}

export function buildEvalScope(
  scopeBundle: ScopeBundle | undefined,
  formValues: Record<string, unknown>
): Record<string, unknown> {
  const safeForm = stripDangerousKeys(formValues);
  const safeBundle = stripDangerousKeys(scopeBundle ?? {});
  const root: Record<string, unknown> = Object.create(null);
  Object.assign(root, safeBundle, safeForm);
  root.form = safeForm;
  return root;
}

export function isJexlString(value: boolean | string | undefined): value is string {
  return typeof value === 'string';
}
